/**
 * Forgot Password
 *
 * Asks for an email and sends a reset link. Same YDM hero treatment as Signin.
 *
 * THE ONE RULE THIS PAGE MUST NOT BREAK: the answer is identical whether or not
 * the address belongs to anyone. A page that says "no such user" is a free tool
 * for working out who banks here, and this product holds clients' ad-account
 * credentials. So there is a single success screen and no error path that
 * depends on the address — see requestPasswordReset, which swallows failures
 * into the log for the same reason.
 *
 * @module pages/auth/ForgotPassword
 */

import React, { useState } from "react";
import { Link } from "react-router-dom";
import { useTranslation, Trans } from "react-i18next";
import { Mail, Loader2, ArrowLeft, MailCheck } from "lucide-react";
import { requestPasswordReset } from "@/config/supabase";
import { logError } from "../../services/logger";
import { YDMWordmark, BrandFooter } from "@/components/brand";
import { Input } from "@/components";

const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

/** Where the emailed link lands. Must be in GOTRUE_URI_ALLOW_LIST. */
const RESET_PATH = "/auth/reset-password";

const ForgotPassword: React.FC = () => {
  const { t } = useTranslation();
  const [email, setEmail] = useState("");
  const [fieldError, setFieldError] = useState<string | undefined>();
  const [isLoading, setIsLoading] = useState(false);
  const [sent, setSent] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    // The only validation is that it LOOKS like an address. Whether it exists is
    // deliberately not checked, here or on the server.
    if (!EMAIL_REGEX.test(email.trim())) {
      setFieldError(t("auth.errors.emailInvalid"));
      return;
    }
    setFieldError(undefined);
    setIsLoading(true);

    try {
      await requestPasswordReset(
        email.trim(),
        `${window.location.origin}${RESET_PATH}`,
      );
    } catch (err) {
      // Never surfaced: a thrown error here would otherwise be the one signal
      // that distinguishes a real address from an invented one.
      logError("Auth", "Password reset request error", err);
    } finally {
      setIsLoading(false);
      setSent(true);
    }
  };

  return (
    <div className="min-h-screen bg-primary-500 flex flex-col items-center justify-center p-4 text-ink-950 relative overflow-hidden">
      <div className="pointer-events-none absolute inset-0">
        <div className="absolute -top-20 -right-20 w-80 h-80 bg-ideas-orange/40 rounded-full blur-2xl" />
        <div className="absolute -bottom-20 -left-20 w-80 h-80 bg-ideas-pink/30 rounded-full blur-2xl" />
      </div>

      <div className="relative max-w-md w-full">
        <div className="text-center mb-8">
          <YDMWordmark variant="hero" bg="yellow" withTagline size="xl" />
          <h1 className="mt-6 font-display italic font-bold text-3xl sm:text-4xl text-ink-950 leading-tight">
            <Trans i18nKey="auth.makeItUnstoppable" />
          </h1>
        </div>

        <div className="bg-white text-ink-900 rounded-2xl shadow-ink border-2 border-ink-950 p-8">
          {sent ? (
            <div role="status" className="text-center">
              <MailCheck className="w-12 h-12 mx-auto text-success-700" aria-hidden="true" />
              <h2 className="mt-4 font-display font-bold text-xl text-ink-950">
                {t("auth.resetSentTitle")}
              </h2>
              {/* Says "if that address has an account" on purpose — it does not
                  confirm that this one does. */}
              <p className="mt-2 text-sm text-ink-700">{t("auth.resetSentBody")}</p>
              <Link
                to="/auth/signin"
                className="mt-6 inline-flex items-center gap-2 text-sm font-bold text-ink-950 underline underline-offset-2 decoration-2 hover:text-ink-700"
              >
                <ArrowLeft className="w-4 h-4" aria-hidden="true" />
                {t("auth.backToSignIn")}
              </Link>
            </div>
          ) : (
            <>
              <h2 className="font-display font-bold text-xl text-ink-950">
                {t("auth.forgotPasswordTitle")}
              </h2>
              <p className="mt-2 mb-6 text-sm text-ink-700">{t("auth.forgotPasswordBody")}</p>

              <form onSubmit={handleSubmit} noValidate className="space-y-6">
                <Input
                  id="email"
                  label={t("auth.emailAddress")}
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="you@example.com"
                  disabled={isLoading}
                  leftIcon={<Mail className="w-5 h-5 text-ink-400" />}
                  error={fieldError}
                  aria-invalid={!!fieldError}
                  className="py-3 border-ink-200 focus:border-primary-500 disabled:bg-ink-100"
                />

                <button
                  type="submit"
                  disabled={isLoading}
                  className="w-full py-3 px-4 bg-ink-950 text-primary-500 font-bold uppercase tracking-wide rounded-lg hover:bg-ink-800 focus:outline-none focus:ring-2 focus:ring-ink-950 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed transition-all flex items-center justify-center gap-2 shadow-ink"
                >
                  {isLoading ? (
                    <>
                      <Loader2 className="w-5 h-5 animate-spin" aria-hidden="true" />
                      {t("auth.sending")}
                    </>
                  ) : (
                    t("auth.sendResetLink")
                  )}
                </button>
              </form>

              <p className="mt-6 text-center text-sm text-ink-700">
                <Link
                  to="/auth/signin"
                  className="font-bold text-ink-950 hover:text-ink-700 underline underline-offset-2 decoration-2"
                >
                  {t("auth.backToSignIn")}
                </Link>
              </p>
            </>
          )}
        </div>

        <BrandFooter />
      </div>
    </div>
  );
};

export default ForgotPassword;
